<template>
  <ui-modal-layout :title="tr('Access Token Request Log')" :icon="NotebookTextIcon">
    <p class="m-0 text-muted-foreground">
      {{ tr('Access token requests made for') }} {{ name }}
      <span class="block font-semibold text-foreground">{{ entityType }} #{{ entityId }}</span>
    </p>

    <div class="glass-panel rounded-xl px-2 py-1">
      <ui-table>
        <ui-table-header>
          <ui-table-row class="hover:bg-transparent [&_th]:text-xs [&_th]:font-semibold [&_th]:text-muted-foreground">
            <ui-table-head>{{ tr('Requested At') }}</ui-table-head>
            <ui-table-head>{{ tr('Support User') }}</ui-table-head>
            <ui-table-head>{{ tr('Level') }}</ui-table-head>
          </ui-table-row>
        </ui-table-header>
        <ui-table-body>
          <ui-table-row v-for="request in requests" :key="request.id">
            <ui-table-cell class="tabular-nums">{{ formatDateTime(request.created_at) }}</ui-table-cell>
            <ui-table-cell>
              <span class="block">
                {{ request.support_user.firstname }}
                <span class="text-muted-foreground">({{ request.support_user.id }})</span>
              </span>
              <span class="block text-xs text-muted-foreground">{{ request.support_user.email }}</span>
            </ui-table-cell>
            <ui-table-cell>{{ request.support_user.level }}</ui-table-cell>
          </ui-table-row>

          <ui-table-row v-if="isLoading || hasMore" ref="scrollPagerTrigger" class="hover:bg-transparent">
            <ui-table-cell v-for="col in 3" :key="col"><ui-skeleton class="h-4 w-20" /></ui-table-cell>
          </ui-table-row>
        </ui-table-body>
      </ui-table>

      <p v-if="requests.length === 0 && !isLoading && !hasMore" class="m-0 px-2 py-6 text-center text-muted-foreground">
        {{ tr('No access token requests have been made for this entity.') }}
      </p>
      <p v-else-if="!hasMore && requests.length > 0" class="m-0 px-2 py-3 text-center text-xs text-muted-foreground">
        {{ tr('End of log') }}
      </p>
    </div>

    <template #footer>
      <ui-button variant="outline" @click="() => $emit('close')">
        {{ tr('Close') }}
      </ui-button>
    </template>
  </ui-modal-layout>
</template>

<script>
import { NotebookText } from '@lucide/vue';
import { markRaw } from 'vue';

import ScrollPagerMixin from '@/mixins/ScrollPager.js';
import SupportService from '@/services/Support';
import { formatDateTime } from '@/utils/date';

const ITEMS_PER_PAGE = 10;

export default {
  name: 'TokenRequestLogModal',
  mixins: [ ScrollPagerMixin ],
  props: {
    entityType: {
      type: String,
      required: true,
    },
    entityId: {
      type: [ Number, String ],
      required: true,
    },
    name: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    NotebookTextIcon: markRaw(NotebookText),
    requests: [],
    page: 0,
    hasMore: true,
    isLoading: false,
  }),
  mounted() {
    this.loadNewData();
  },
  methods: {
    formatDateTime,
    async loadNewData() {
      if (this.isLoading || !this.hasMore) {
        return;
      }

      this.isLoading = true;

      try {
        ++this.page;
        const response = await SupportService.getTokenRequestLog(
          this.entityType,
          this.entityId,
          this.page,
          ITEMS_PER_PAGE,
        );

        this.requests.push(...response);
        this.hasMore = response.length === ITEMS_PER_PAGE;

        if (this.hasMore) {
          this.$nextTick(() => this.initScrollPager('scrollPagerTrigger', 'loadNewData'));
        }
      }
      catch (error) {
        this.hasMore = false;
        this.error(error);
      }
      finally {
        this.isLoading = false;
      }
    },
  },
};
</script>
